import { useEffect, useState } from 'react';
import {
  Wallet,
  Eye,
  FileText,
  X,
  Monitor,
  Smartphone,
  Lightbulb,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import type {
  CreativePreview,
  ManualCampaignForm,
  PreviewData,
  SelectedScreenMeta,
} from '@/features/campaigns/types/campaign';
import { formatMoney } from '@/shared/lib/format/money';
import { resolvePreviewBill } from '@/features/campaigns/components/PreviewStep/previewBilling';
import {
  resolvePreviewDevice,
  type PreviewDeviceKind,
} from './constants';
import { PhysicalScreenPreview } from './PhysicalScreenPreview';
import {
  buildDeviceFitAdvice,
  normalizeDetectedOrientation,
  resolvePreviewObjectFit,
} from './orientationAssist';
import { MbBadge, MbHelpTooltip } from './primitives';

type LivePreviewPanelProps = {
  currentStep: number;
  campaignData: ManualCampaignForm;
  setCampaignData?: React.Dispatch<React.SetStateAction<ManualCampaignForm>>;
  creativePreview: CreativePreview;
  setCreativePreview?: React.Dispatch<React.SetStateAction<CreativePreview>>;
  errors?: Record<string, string>;
  clearError?: (field: string) => void;
  selectedScreensMeta: Record<string, SelectedScreenMeta>;
  previewData?: PreviewData;
  detectingOrientation?: boolean;
  onDetectingChange?: (busy: boolean) => void;
  availableBalance?: number;
  onClose?: () => void;
};

const DEVICE_TABS: {
  id: PreviewDeviceKind;
  label: string;
  short: string;
  Icon: typeof Monitor;
}[] = [
  { id: 'landscape', label: 'Landscape', short: '16:9', Icon: Monitor },
  { id: 'portrait', label: 'Portrait', short: '9:16', Icon: Smartphone },
];

function firstSelectedMeta(meta: Record<string, SelectedScreenMeta>) {
  const values = Object.values(meta);
  return values[0];
}

function deviceLabel(device: PreviewDeviceKind) {
  if (device === 'portrait') return 'Portrait 9:16 Display';
  return 'Landscape 16:9 Display';
}

export function LivePreviewPanel({
  currentStep,
  campaignData,
  setCampaignData: _setCampaignData,
  creativePreview,
  setCreativePreview: _setCreativePreview,
  errors: _errors,
  clearError: _clearError,
  selectedScreensMeta,
  previewData,
  detectingOrientation = false,
  onDetectingChange: _onDetectingChange,
  availableBalance,
  onClose,
}: LivePreviewPanelProps) {
  const [activeTab, setActiveTab] = useState<'preview' | 'summary'>('preview');
  const [deviceOverride, setDeviceOverride] = useState<PreviewDeviceKind | null>(
    null,
  );

  const creativeOrientation = normalizeDetectedOrientation(
    campaignData.mediaIds?.[0]?.orientation,
  );
  const selected = firstSelectedMeta(selectedScreensMeta);
  const screenOrientation = String(
    selected?.screenOrientation || '',
  ).toUpperCase();

  const suggestedDevice = resolvePreviewDevice({
    creativeOrientation,
    screenOrientation,
  });

  // Follow creative/screen when user has not picked a device tab yet.
  // Reset override when detected orientation changes so preview stays useful.
  useEffect(() => {
    setDeviceOverride(null);
  }, [creativeOrientation]);

  const device = deviceOverride ?? suggestedDevice;
  const objectFit = resolvePreviewObjectFit(creativeOrientation, device);
  const fitAdvice = buildDeviceFitAdvice({
    creative: creativeOrientation,
    device,
    detecting: detectingOrientation,
  });

  const bill = resolvePreviewBill(previewData);
  const hasPricing = Boolean(
    previewData?.pricing?.amountWithTax != null ||
      previewData?.pricing?.totalAmount != null,
  );
  const screenCount = Object.keys(selectedScreensMeta).length;

  return (
    <aside className="mb-preview-panel flex h-full max-h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-[var(--dash-line)] bg-white shadow-[0_16px_40px_-20px_rgba(16,38,73,0.18)]">
      <div className="shrink-0 flex items-center justify-between border-b border-[var(--dash-line)] bg-[#FAFBFD] px-4 pt-2">
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => setActiveTab('preview')}
            className={`relative flex items-center gap-1.5 pb-2.5 text-xs font-semibold transition ${
              activeTab === 'preview'
                ? 'text-[var(--dash-brand)]'
                : 'text-[var(--dash-muted)] hover:text-[var(--dash-ink)]'
            }`}
          >
            <Eye className="h-3.5 w-3.5" />
            <span>Preview</span>
            {activeTab === 'preview' ? (
              <span className="absolute bottom-0 inset-x-0 h-0.5 rounded-full bg-[var(--dash-brand)]" />
            ) : null}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('summary')}
            className={`relative flex items-center gap-1.5 pb-2.5 text-xs font-semibold transition ${
              activeTab === 'summary'
                ? 'text-[var(--dash-brand)]'
                : 'text-[var(--dash-muted)] hover:text-[var(--dash-ink)]'
            }`}
          >
            <FileText className="h-3.5 w-3.5" />
            <span>Summary</span>
            {activeTab === 'summary' ? (
              <span className="absolute bottom-0 inset-x-0 h-0.5 rounded-full bg-[var(--dash-brand)]" />
            ) : null}
          </button>
        </div>

        <div className="pb-2 flex items-center gap-1.5">
          {detectingOrientation ? (
            <MbBadge tone="brand">Analyzing orientation…</MbBadge>
          ) : creativeOrientation !== 'EMPTY' &&
            creativeOrientation !== 'UNKNOWN' ? (
            <MbBadge tone="brand">{creativeOrientation} creative</MbBadge>
          ) : creativeOrientation === 'UNKNOWN' ? (
            <MbBadge tone="warn">Manual selection</MbBadge>
          ) : (
            <MbBadge tone="muted">Screen Preview</MbBadge>
          )}

          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-6 w-6 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-800"
              aria-label="Close preview"
              title="Close preview"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      {activeTab === 'preview' ? (
        <div className="flex min-h-0 flex-1 flex-col justify-between overflow-hidden p-3">
          <div className="mb-2 shrink-0">
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold text-[var(--dash-ink)]">
                Device preview
              </span>
              <MbHelpTooltip label="About device preview">
                Switch Landscape or Portrait to see how your creative plays on
                Screenista displays. When orientations differ, the preview
                stretches to fill — the same adaptation used on devices.
              </MbHelpTooltip>
            </div>
            <div
              role="tablist"
              aria-label="Preview device orientation"
              className="grid grid-cols-2 gap-1 rounded-xl bg-[var(--dash-soft)] p-1"
            >
              {DEVICE_TABS.map(({ id, label, short, Icon }) => {
                const selectedTab = device === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={selectedTab}
                    onClick={() => setDeviceOverride(id)}
                    className={`flex flex-col items-center gap-0.5 rounded-lg px-1.5 py-1.5 transition ${
                      selectedTab
                        ? 'bg-white text-[var(--dash-ink)] shadow-xs'
                        : 'text-[var(--dash-muted)] hover:text-[var(--dash-ink)]'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" aria-hidden />
                    <span className="text-[11px] font-semibold leading-none">
                      {label}
                    </span>
                    <span className="text-[9px] font-medium leading-none opacity-70">
                      {short}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="relative flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden py-1">
            <PhysicalScreenPreview
              device={device}
              creative={creativePreview}
              detecting={detectingOrientation}
              objectFit={objectFit}
            />
            {objectFit === 'cover' &&
            creativePreview.url &&
            !detectingOrientation ? (
              <span className="pointer-events-none absolute left-2 top-2 rounded-md bg-amber-500/95 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow-sm">
                Stretched to fill
              </span>
            ) : null}
          </div>

          {fitAdvice ? (
            <div
              className={`mt-2 shrink-0 rounded-xl border px-2.5 py-2 ${
                fitAdvice.tone === 'success'
                  ? 'border-emerald-200 bg-emerald-50/80'
                  : fitAdvice.tone === 'warn'
                    ? 'border-amber-200 bg-amber-50/90'
                    : 'border-[var(--dash-line)] bg-[var(--dash-soft)]/80'
              }`}
            >
              <div className="flex items-start gap-2">
                {fitAdvice.tone === 'success' ? (
                  <CheckCircle2
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600"
                    aria-hidden
                  />
                ) : fitAdvice.tone === 'warn' ? (
                  <AlertTriangle
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600"
                    aria-hidden
                  />
                ) : (
                  <Lightbulb
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--dash-brand)]"
                    aria-hidden
                  />
                )}
                <div className="min-w-0">
                  <p className="m-0 text-[11px] font-semibold text-[var(--dash-ink)]">
                    {fitAdvice.title}
                  </p>
                  <p className="m-0 mt-0.5 text-[10px] leading-snug text-[var(--dash-muted)]">
                    {fitAdvice.body}
                  </p>
                  <p className="m-0 mt-1 text-[10px] font-semibold leading-snug text-[var(--dash-ink)]">
                    Suggestion: {fitAdvice.suggestion}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-2 shrink-0 flex items-center justify-between rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)]/70 px-3 py-1.5 text-xs">
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${
                    campaignData.mediaIds?.[0]?.mediaId
                      ? 'bg-emerald-500'
                      : 'bg-slate-300'
                  }`}
                />
                <span className="truncate font-semibold text-[var(--dash-ink)]">
                  {campaignData.mediaIds?.[0]?.mediaId
                    ? 'Live display simulation'
                    : 'Simulator standby — upload a creative'}
                </span>
              </div>
              <span className="shrink-0 text-[11px] font-semibold text-[var(--dash-muted)]">
                {campaignData.customDuration || '30'}s
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-1 flex-col overflow-y-auto p-4">
          <div className="mb-3 flex items-center gap-2">
            <Wallet className="h-4 w-4 text-[var(--dash-brand)]" aria-hidden />
            <h3 className="m-0 text-[14px] font-semibold text-[var(--dash-ink)]">
              Campaign Configuration
            </h3>
            <MbHelpTooltip label="About billing summary">
              Final totals and GST are verified by the campaign booking API after
              slots are selected.
            </MbHelpTooltip>
          </div>

          <dl className="m-0 space-y-2 text-[12px]">
            <SummaryRow
              label="Campaign name"
              value={campaignData.campaignName || '—'}
            />
            <SummaryRow
              label="Objective"
              value={campaignData.campaignGoals || '—'}
            />
            <SummaryRow label="Ad Category" value={campaignData.adType || '—'} />
            <SummaryRow
              label="Cities"
              value={campaignData.targetCities.join(', ') || '—'}
            />
            <SummaryRow
              label="Schedule"
              value={
                campaignData.startDate && campaignData.endDate
                  ? `${campaignData.startDate} → ${campaignData.endDate}`
                  : '—'
              }
            />
            <SummaryRow
              label="Screens count"
              value={screenCount ? String(screenCount) : '—'}
            />
            <SummaryRow
              label="Ad Duration"
              value={
                campaignData.customDuration
                  ? `${campaignData.customDuration}s`
                  : '—'
              }
            />
            <SummaryRow
              label="Preview format"
              value={deviceLabel(device)}
            />
            <SummaryRow
              label="Creative fit"
              value={
                objectFit === 'cover'
                  ? 'Stretched to fill (mismatch)'
                  : 'Native contain'
              }
            />
          </dl>

          <div className="mt-4 border-t border-[var(--dash-line)] pt-3">
            {hasPricing && currentStep >= 4 ? (
              <div className="rounded-xl bg-[var(--dash-soft)] p-3">
                <p className="m-0 text-[11px] text-[var(--dash-muted)]">
                  Total payable
                </p>
                <p className="m-0 text-2xl font-bold tracking-tight text-[var(--dash-ink)]">
                  {formatMoney(bill.payable)}
                </p>
                <div className="mt-2 space-y-1 text-[12px]">
                  <SummaryRow
                    label="Subtotal"
                    value={formatMoney(bill.subtotal)}
                  />
                  {bill.discount > 0 ? (
                    <SummaryRow
                      label="Discount"
                      value={`−${formatMoney(bill.discount)}`}
                      tone="success"
                    />
                  ) : null}
                  {bill.taxRows.length > 0 ? (
                    bill.taxRows.map((row) => (
                      <SummaryRow
                        key={row.key}
                        label={row.label}
                        value={formatMoney(row.amount)}
                      />
                    ))
                  ) : bill.totalTax > 0 ? (
                    <SummaryRow
                      label="GST"
                      value={formatMoney(bill.totalTax)}
                    />
                  ) : null}
                  {availableBalance != null ? (
                    <SummaryRow
                      label="Wallet balance"
                      value={formatMoney(availableBalance)}
                    />
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-[var(--dash-line)] bg-[var(--dash-soft)] p-3 text-center">
                <p className="m-0 text-[12px] font-semibold text-[var(--dash-ink)]">
                  Pricing calculated after slots
                </p>
                <p className="m-0 mt-1 text-[11px] leading-relaxed text-[var(--dash-muted)]">
                  Verified screen totals and GST will populate automatically after
                  completing Step 3.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}

function SummaryRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'success';
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-[var(--dash-muted)]">{label}</dt>
      <dd
        className={`m-0 text-right font-semibold ${
          tone === 'success'
            ? 'text-[var(--dash-success)]'
            : 'text-[var(--dash-ink)]'
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
